import type { AppSettings, SleepTimes } from '../data/app_settings.ts';

// When the screen is asleep (docs/specs/settings.md §4.4): blanked during the sleep times (AP-3),
// dimmed after some minutes without a touch (AP-5), awake otherwise. It follows from the clock and
// the last time someone touched the screen or came near, so there is no state to keep in step.

export type ScreenState = 'awake' | 'dimmed' | 'blanked';

/** How long a touch, or someone near, wakes a blanked screen (AP-3). */
export const WAKE_MS = 60_000;

const minutesOf = (time: string) : number => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

/** Whether `now` falls between the sleep times; across midnight when `to` comes before `from`. */
export function isSleepTime(now: Date, sleep: SleepTimes) : boolean {
    if (!sleep.enabled) {
        return false;
    }

    const from = minutesOf(sleep.from);
    const to = minutesOf(sleep.to);
    const minute = now.getHours() * 60 + now.getMinutes();

    return from <= to ? minute >= from && minute < to : minute >= from || minute < to;
}

/**
 * The screen's state at `now`, with `lastActivity` the time (ms) of the last touch or someone near.
 * Without keep awake the system turns the screen off itself, and Tilez leaves it alone (AP-7).
 */
export function screenState(now: Date, settings: AppSettings, lastActivity: number) : ScreenState {
    if (!settings.keepAwake) {
        return 'awake';
    }

    const idle = now.getTime() - lastActivity;
    if (isSleepTime(now, settings.sleep)) {
        return idle < WAKE_MS ? 'awake' : 'blanked';
    }

    return settings.dim.enabled && idle >= settings.dim.minutes * 60_000 ? 'dimmed' : 'awake';
}

/** The brightness for a state: low when dimmed (AP-5), lowest when blanked, the system's when awake. */
export function brightnessFor(state: ScreenState) : number | null {
    switch (state) {
        case 'blanked':
            return 0;
        case 'dimmed':
            return 0.1;
        case 'awake':
            return null;
    }
}
